import React, { useRef, useState, useEffect, useCallback, useImperativeHandle,} from 'react';
import { Stage, Layer, Image as KonvaImage, Line } from 'react-konva';
import useImage from 'use-image';
import { Button } from 'primereact/button';

const MAX_WIDTH = 480; // ancho máximo en desktop
const ASPECT_DEFAULT = 360 / 480; // fallback antes de que cargue la imagen


function VehiculoDanosCanvas({ siluetaUrl, trazosIniciales = [], onChange, ref }) {
  const [image] = useImage(siluetaUrl, 'anonymous');
  const [lineas, setLineas] = useState(trazosIniciales); // puntos normalizados 0..1
  const [anchoContenedor, setAnchoContenedor] = useState(MAX_WIDTH);
  const dibujando = useRef(false);
  const contenedorRef = useRef(null);
  const stageRef = useRef(null);
  const lineasRef = useRef(trazosIniciales); // espejo síncrono de `lineas`, para leer el valor actual sin depender del closure

  useEffect(() => {
    const el = contenedorRef.current;
    if (!el) return;
    const actualizarAncho = () => setAnchoContenedor(Math.min(el.offsetWidth, MAX_WIDTH));
    actualizarAncho();
    const resizeObserver = new ResizeObserver(actualizarAncho);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  const aspect = image ? image.height / image.width : ASPECT_DEFAULT;
  const displayWidth = anchoContenedor;
  const displayHeight = Math.round(anchoContenedor * aspect);

  // Convierte una posición de pantalla (clientX/Y) a coordenadas normalizadas 0..1
  const obtenerPuntoNormalizado = useCallback((evt) => {
    const stage = stageRef.current;
    if (!stage) return null;
    const rect = stage.container().getBoundingClientRect();

    let clientX, clientY;
    if (evt.touches && evt.touches.length > 0) {
      clientX = evt.touches[0].clientX;
      clientY = evt.touches[0].clientY;
    } else {
      clientX = evt.clientX;
      clientY = evt.clientY;
    }

    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    return { x, y };
  }, []);

  const handleMouseDown = useCallback((e) => {
    const punto = obtenerPuntoNormalizado(e.evt);
    if (!punto) return;
    dibujando.current = true;
    setLineas((prev) => {
      const nuevas = [...prev, { puntos: [punto.x, punto.y] }];
      lineasRef.current = nuevas;
      return nuevas;
    });
  }, [obtenerPuntoNormalizado]);

  const handleMouseMove = useCallback((e) => {
    if (!dibujando.current) return;
    const punto = obtenerPuntoNormalizado(e.evt);
    if (!punto) return;
    setLineas((prev) => {
      const nuevas = [...prev];
      const ultima = { ...nuevas[nuevas.length - 1] };
      ultima.puntos = ultima.puntos.concat([punto.x, punto.y]);
      nuevas[nuevas.length - 1] = ultima;
      lineasRef.current = nuevas;
      return nuevas;
    });
  }, [obtenerPuntoNormalizado]);

  const handleMouseUp = useCallback((e) => {
    if (e?.evt) e.evt.preventDefault();
    if (!dibujando.current) return;
    dibujando.current = false;
    onChange?.(lineasRef.current);
  }, [onChange]);

  const deshacer = () => {
    const nuevas = lineas.slice(0, -1);
    lineasRef.current = nuevas;
    setLineas(nuevas);
    onChange?.(nuevas);
  };

  const limpiar = () => {
    lineasRef.current = [];
    setLineas([]);
    onChange?.([]);
  };

  // Convierte los puntos normalizados (0..1) a píxeles del canvas actual, para dibujarlos
  const puntosAPixeles = (puntosNormalizados) =>
    puntosNormalizados.map((valor, i) =>
      i % 2 === 0 ? valor * displayWidth : valor * displayHeight
    );

  useImperativeHandle(ref, () => ({
    exportarComoImagen: () => stageRef.current.toDataURL({ pixelRatio: 2 }),
  }));

  return (
    <div>
      <div className="d-flex gap-2 mb-2">
        <Button type="button" label="Deshacer" icon="pi pi-undo"
          className="btn-outline-azul" onClick={deshacer}
          disabled={lineas.length === 0} />
        <Button type="button" label="Limpiar" icon="pi pi-trash"
          className="btn-outline-rojo" onClick={limpiar}
          disabled={lineas.length === 0} />
      </div>

      <div
        ref={contenedorRef}
        style={{ 
          border: '1px solid #ddd', 
          backgroundColor: '#ffffff',
          borderRadius: 8,
          width: '100%',
          maxWidth: MAX_WIDTH,
          touchAction: 'none'
        }}
      >
        <Stage
          ref={stageRef}
          width={displayWidth}
          height={displayHeight}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchMove={handleMouseMove}
          onTouchEnd={handleMouseUp}
        >
          <Layer>
            {image && <KonvaImage image={image} width={displayWidth} height={displayHeight} />}
            {lineas.map((linea, i) => (
              <Line
                key={i}
                points={puntosAPixeles(linea.puntos)}
                stroke="#e63946"
                strokeWidth={3}
                tension={0.3}
                lineCap="round"
                lineJoin="round"
              />
            ))}
          </Layer>
        </Stage>
      </div>

      <small className="text-muted d-block mt-1">
        Dibuja sobre la imagen para marcar los daños del vehículo.
      </small>
    </div>
  );
}

export default VehiculoDanosCanvas;